"use client";

import type {
  IResource,
  WorkplaceRequestOccupiedResourceAction
} from "@gettactic/api";
import { UnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import React from "react";
import { useResourceDedicatedUsers } from "@/features/map/hooks/useResourceDedicatedUsers";
import {
  type DeskAutomationStep,
  OCCUPIED_DESK_ACTION_LABEL,
  readOccupiedResourceAction,
  shouldOfferTakenChoice
} from "@/features/workplace-requests/lib/desk-automation";
import { useCanReadResourceOwners } from "@/lib/useCanReadResourceOwners";

const OPTIONS = (["replace", "add_seat"] as const).map((value) => ({
  id: value,
  name: OCCUPIED_DESK_ACTION_LABEL[value]
}));

export interface TakenDeskChoiceListProps {
  /** The desk moves this approval will run, already filtered to the enabled ones. */
  steps: DeskAutomationStep[];
  /** The resources those moves target, as far as they have loaded. */
  deskResources: IResource[];
  /** Approver picks, by workflow id. A missing entry means the rule's own choice. */
  overrides: Record<string, WorkplaceRequestOccupiedResourceAction>;
  onChange: (
    workflowId: string,
    action: WorkplaceRequestOccupiedResourceAction
  ) => void;
  /** Freezes the selects while the approval is in flight. */
  isDisabled?: boolean;
}

/**
 * What a dedicated move does when its desk already has every owner it can
 * hold, editable for this one approval. Whether to evict someone or make room
 * depends on facts the rule can't know, so the approver gets the last word.
 * Each row decides on its own whether the desk is full.
 */
export const TakenDeskChoiceList: React.FC<TakenDeskChoiceListProps> = ({
  steps,
  deskResources,
  overrides,
  onChange,
  isDisabled
}) => (
  <>
    {steps
      .filter((step) => step.action === "assign" && step.mode === "dedicated")
      .map((step) => (
        <TakenDeskChoiceRow
          key={step.workflowId}
          step={step}
          resource={deskResources.find((r) => r.id === step.resourceId) ?? null}
          value={overrides[step.workflowId] ?? step.occupiedResourceAction}
          isOverridden={step.workflowId in overrides}
          onChange={(action) => onChange(step.workflowId, action)}
          isDisabled={isDisabled}
        />
      ))}
  </>
);

interface TakenDeskChoiceRowProps {
  step: DeskAutomationStep;
  resource: IResource | null;
  value: WorkplaceRequestOccupiedResourceAction;
  isOverridden: boolean;
  onChange: (action: WorkplaceRequestOccupiedResourceAction) => void;
  isDisabled?: boolean;
}

const TakenDeskChoiceRow: React.FC<TakenDeskChoiceRowProps> = ({
  step,
  resource,
  value,
  isOverridden,
  onChange,
  isDisabled
}) => {
  // Without access to the owners the desk can't be judged full, so the choice
  // is offered anyway rather than waiting on a lookup that cannot succeed.
  const canReadOwners = useCanReadResourceOwners();
  const owners = useResourceDedicatedUsers(
    canReadOwners ? step.resourceId : null
  );
  const target =
    resource && owners.data
      ? { name: resource.name, resource, owners: owners.data }
      : null;
  if (!shouldOfferTakenChoice(step, target, owners.isLoading)) return null;

  return (
    <div className="mt-3 rounded-brand border border-indigo-200 bg-white p-3">
      <UnifiedSelectInput
        label={`If ${resource?.name ?? "the desk"} is already taken`}
        helperText={
          isOverridden
            ? "Applies to this request only — the request type keeps its own setting."
            : "Set on the request type. Change it here to do something different just this once."
        }
        items={OPTIONS}
        value={value}
        onChangeValue={(next) => onChange(readOccupiedResourceAction(next))}
        disabled={isDisabled}
      />
    </div>
  );
};
